import {type WatchSource, ref, watch} from 'vue';
import debounce from 'lodash-es/debounce';
import {useAbortable, useLogger, useNuxtApp, useUniversalFetchRaw} from '#imports';

export const RECOGNIZED_PROMPT_ENTITY_KEYS = ['jobTitle', 'dailyRate', 'industry', 'location', 'experience', 'skills'] as const;

export type RecognizedPromptEntityKey = (typeof RECOGNIZED_PROMPT_ENTITY_KEYS)[number];

export type RecognizedPromptEntities = Record<RecognizedPromptEntityKey, boolean>;

export const DEFAULT_RECOGNIZED_PROMPT_ENTITIES: RecognizedPromptEntities = Object.fromEntries(
    RECOGNIZED_PROMPT_ENTITY_KEYS.map((key) => [key, false]),
) as RecognizedPromptEntities;

/**
 * Watches a prompt ref and calls the NER endpoint to surface real-time entity
 * hints (jobTitle, dailyRate, …) while the user types.
 *
 * Two implementation choices deserve a note:
 *
 * 1. `useUniversalFetchRaw` (instead of the typed `@api` client):
 *    this composable lives in a shared Nuxt module that has no access to
 *    `@api` (the OpenAPI client is generated per-app). Hence the raw fetch +
 *    a locally declared `RecognizedPromptEntities` type matching the contract
 *    (`DetectedPromptEntities` in search-api-contract).
 *
 * 2. `useNuxtApp()` + `nuxtApp.runWithContext(...)`:
 *    `debounce` schedules its callback via `setTimeout`, which breaks Nuxt
 *    context propagation (AsyncLocalStorage doesn't traverse timers). Without
 *    this, `useUniversalFetchRaw` (which calls `useNuxtApp()` internally for
 *    cookies, CSRF, forwarded headers...) would throw "Nuxt instance
 *    unavailable". Capturing the instance synchronously and re-injecting it
 *    around the fetch fixes this.
 *
 *    Note: Nuxt's `experimental.asyncContext: true` mitigates the issue on
 *    Node SSR but (a) is not enabled by every app consuming this module
 *    (e.g. cms-front), and (b) has no effect on the client where the
 *    debounced fetch actually runs in practice. So the wrapping stays.
 */

const DEBOUNCE_MS = 300;
const MIN_PROMPT_LENGTH = 3;
const DETECT_PROMPT_ENTITIES_URL = '/search/api/advanced/prompt-entities';

export function usePromptEntities(prompt: WatchSource<string>) {
    const logger = useLogger();
    // Captured synchronously while the Nuxt context is still active; reused below
    // in `runWithContext` to keep `useUniversalFetchRaw` working after debounce.
    const nuxtApp = useNuxtApp();
    // `useAbortable` handles the AbortController lifecycle: it auto-aborts the
    // previous request when a new one starts, and returns `undefined` (instead
    // of throwing) when the in-flight call is aborted.
    const {execute, abort} = useAbortable();

    const recognizedPromptEntities = ref<RecognizedPromptEntities | null>(null);

    const debouncedFetch = debounce(async (currentPrompt: string) => {
        try {
            const response = await execute((signal) =>
                nuxtApp.runWithContext(() =>
                    useUniversalFetchRaw<RecognizedPromptEntities>(DETECT_PROMPT_ENTITIES_URL, {
                        method: 'POST',
                        body: {prompt: currentPrompt},
                        signal,
                        baseURL: '',
                    }),
                ),
            );
            if (response) {
                recognizedPromptEntities.value = response._data ?? null;
            }
        } catch (e) {
            recognizedPromptEntities.value = null;
            logger.warn('Failed to detect prompt entities', e);
        }
    }, DEBOUNCE_MS);

    watch(
        prompt,
        (currentPrompt) => {
            const trimmedPrompt = (currentPrompt ?? '').trim();
            if (trimmedPrompt.length < MIN_PROMPT_LENGTH) {
                debouncedFetch.cancel();
                abort();
                recognizedPromptEntities.value = null;
                return;
            }
            debouncedFetch(trimmedPrompt);
        },
        {immediate: true},
    );

    return {recognizedPromptEntities};
}
